import React from 'react';
import Autocomplete, 
{ AutocompleteChangeReason, 
  AutocompleteRenderInputParams, 
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemButton,
  ListItemIcon,
  MenuItem,
  Popper,
  Select,
  SelectChangeEvent,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon'  ;
import useScreenSize from '../../../utils/useScreenSize';
import { useTranslation } from 'react-i18next';
import { SearchCategory } from '../SearchBar';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import useOnboardingStepCounter from '../../../utils/onboardingStepCounter';

export const zIndex = {
  searchListItem: 1000,
  searchSuggestions: 3000,
} as const;

export enum SuggestionType {
  RecentSearch = 'recentSearch',
  SearchSuggestion = 'searchSuggestion',
}

export type Suggestion = {
  label: string;
  value: string;
  type: SuggestionType;
};

interface SearchBarInputBoxProps {
  suggestions: Suggestion[];
  placeholder: string;
  onSearch: (searchTerm: string) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  setCategory: (category: SearchCategory) => void;
  category: SearchCategory;
  onboardingStepCounter?: ReturnType<typeof useOnboardingStepCounter>;
}

interface InputBoxProps {
  renderInputParams: AutocompleteRenderInputParams
  placeholder: string;
  onSearch: (searchTerm: string) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  setCategory: (category: SearchCategory) => void;
  category: SearchCategory;
}

interface AutoCompleteSuggestionProps {
  option: Suggestion;
  onSearch: (searchTerm: string) => void;
  setInputValue: (value: string) => void;
  inputValue: string;
  isDesktop: boolean;
  onClick: () => void;
}

interface AutocompleteInputProps {
  suggestions: Suggestion[];
  inputValue: string;
  setInputValue: (value: string) => void;
  setCategory: (category: SearchCategory) => void;
  category: SearchCategory;
  placeholder: string;
  onSearch: (searchTerm: string) => void;
}

const InputBox: React.FC<InputBoxProps> = 
({ renderInputParams, placeholder, onSearch, inputValue, setInputValue, setCategory, category }) => {
  const { t } = useTranslation();
  const { isSmallScreen } = useScreenSize();
  
  const handleSearch = () => {
    onSearch(inputValue);
  };

  const EndAdornment = () => (
    <InputAdornment position='end'>
      {
        inputValue && 
        <IconButton
          onClick={() => setInputValue('')}
          data-testid='search-bar-clear-button'
        >
          <SvgIcon 
            component={getMuiIcon('close')} 
            sx={{ width: 20, height: 20, color: palette.text.secondary }}
          />
        </IconButton>
      }
      <IconButton onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        handleSearch();
      }}>
        <SvgIcon 
          component={getMuiIcon('search')} 
          sx={{ width: 20, height: 20, color: palette.text.secondary }}
          viewBox='0 0 17 17'
          data-testid='search-bar-search-button'
        />
      </IconButton>
    </InputAdornment>
  );

  const MobileStartAdornment = () => (
    <InputAdornment position='start'>
      <Select
        data-testid='search-bar-category-selector'
        value={category}
        onChange={(e: SelectChangeEvent) => setCategory(e.target.value as SearchCategory)}
        IconComponent={() => <></>}
        renderValue={() => (
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'center' }}>
            <SvgIcon component={getMuiIcon('alleTab')} />
            <SvgIcon component={getMuiIcon('arrowDropDown')} />
          </Box>
        )}
        sx={{
          height: '40px',
          paddingLeft: `${spaces.sm}!important`,
          paddingRight: `${spaces.xxs}!important`,
          width: '64px!important',
          backgroundColor: palette.common.white,
          borderRadius: borderRadius.full,
          border: `1px solid ${palette.elevation.outlined}`,
          cursor: 'pointer',
          '& .MuiSelect-select': {
            paddingX: '0px!important',
            paddingY: spaces.xs,
          },
          '&:focus, &:hover, &.Mui-focused': {
            border: `2px solid ${palette.primary.main}`,
          },
          '& fieldset': {
            border: 'none!important',
          }
        }}
        MenuProps={{
          sx: {
            marginTop: spaces.xxs,
            zIndex: `${zIndex.searchSuggestions}!important`,
            '& .MuiPaper-root': {
              elevation: 8,
              borderRadius: borderRadius.md,
              border: `1px solid ${palette.elevation.outlined}`,
              paddingX: spaces.xs,
            },
          },
          MenuListProps: {
            sx: {
              '& .MuiMenuItem-root': {
                borderRadius: borderRadius.sm,
                '&:hover, &:focus, &.Mui-selected': {
                  backgroundColor: `${palette.primary.selected}!important`,
                }
              }
            }
          }
        }}
      >
        <MenuItem value='Library'>{t('Library')}</MenuItem>
        <MenuItem value='Professional Development'>{t('Professional Development')}</MenuItem>
      </Select>
    </InputAdornment>
  );

  const DesktopStartAdornment = () => (
    <InputAdornment position='start'>
      <Select
        data-testid='search-bar-category-selector'
        value={category}
        onChange={(e: SelectChangeEvent) => setCategory(e.target.value as SearchCategory)}
        sx={{
          width: '160px!important',
          height: '40px',
          paddingLeft: `${spaces.md}!important`,
          paddingRight: `${spaces.md}!important`,
          backgroundColor: palette.common.white,
          borderRadius: borderRadius.full,
          border: `1px solid ${palette.elevation.outlined}`,
          cursor: 'pointer',
          '& .MuiSelect-select': {
            paddingX: '0px!important',
            paddingY: spaces.xs,
          },
          '&:focus, &:hover, &.Mui-focused': {
            border: `2px solid ${palette.primary.main}`,
          },
          '& fieldset': {
            border: 'none!important',
          }
        }}
        MenuProps={{
          sx: {
            marginTop: spaces.xxs,
            zIndex: `${zIndex.searchSuggestions}!important`,
            '& .MuiPaper-root': {
              elevation: 8,
              borderRadius: borderRadius.md,
              border: `1px solid ${palette.elevation.outlined}`,
              paddingX: spaces.xs,
            },
          },
          MenuListProps: {
            sx: {
              '& .MuiMenuItem-root': {
                borderRadius: borderRadius.sm,
                '&:hover, &:focus, &.Mui-selected': {
                  backgroundColor: `${palette.primary.selected}!important`,
                }
              }
            }
          }
        }}
      >
        <MenuItem value='Library'>{t('Library')}</MenuItem>
        <MenuItem value='Professional Development'>{t('Professional Development')}</MenuItem>
      </Select>
    </InputAdornment>
  );

  return (
    <TextField
      autoFocus={isSmallScreen}
      {...renderInputParams}
      size='small'
      onChange={(e) => {
        setInputValue(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          handleSearch();        
        } 
      }}
      InputProps={{
        ...renderInputParams.InputProps, 
        placeholder,
        endAdornment: <EndAdornment />,
        startAdornment: isSmallScreen ? <MobileStartAdornment /> : <DesktopStartAdornment />,
        sx: {
          paddingRight: `${spaces.md}!important`,
          paddingLeft: '0px!important',
          borderRadius: borderRadius.full,
          backgroundColor: palette.background.default,
          '&:hover': {
            backgroundColor: palette.background.paperElevation1,
          },
          '&.Mui-focused': {
            backgroundColor: palette.background.paperElevation1,
          },
          '& .MuiOutlinedInput-notchedOutline': {  // Unfocused
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { // On Hover
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${palette.primary.outlinedBorder}`, // Focused
          },
        },
      }}
    />
  );
};

export const AutoCompleteSuggestion = 
({ option, onSearch, isDesktop, inputValue, setInputValue }: AutoCompleteSuggestionProps) => {
  const matches = match(option.label, inputValue, { insideWords: true });
  const parts = parse(option.label, matches);
  const isRecentSearch = option.type === SuggestionType.RecentSearch;
  return (
    <ListItem
      key={option.value}
      value={option.value}
      disablePadding
      sx={{ zIndex: `${zIndex.searchListItem}!important` }}
    >
      <ListItemButton
        onClick={() => {
          setInputValue(option.value);
          onSearch(option.value);
        }}
        sx={{
          paddingX: spaces.md,
          paddingY: spaces.sm,
          borderRadius: borderRadius.sm,
          '&:hover': { backgroundColor: isDesktop ? palette.action.hover : palette.primary.selected },
        }}
      >
        <ListItemIcon sx={{ minWidth: '36px', padding: 0, margin: 0, color: palette.text.secondary }}>
          {isRecentSearch ? (
            <SvgIcon
              data-testid='recent-search-icon'
              component={getMuiIcon('clock')} sx={{
                fill: 'transparent',
              }} />
          ) : (
            <SvgIcon
              data-testid='search-suggestion-icon'
              component={getMuiIcon('search')}
              viewBox='0 0 17 17'
              sx={{ width: '24px', height: '24px', color: palette.text.secondary }} />
          )}
        </ListItemIcon>
        <Box data-testid='search-bar-suggestion'>
          {
            parts.map((part, index) => (
              <Typography
                key={index}
                sx={{
                  fontWeight: (part.highlight || inputValue === '') ? 400 : 600,  // Highlight the unmatched part 
                  color: palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                component='span'
                data-testid={
                  (part.highlight || inputValue === '') ? 
                    'search-bar-suggestion-matched' : 'search-bar-suggestion-unmatched'
                }
              >
                {part.text}
              </Typography>
            ))
          }
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

const SearchBarInputBox: React.FC<SearchBarInputBoxProps> = 
({ inputValue, setInputValue, suggestions, placeholder, onSearch, category, setCategory }) => {
  const { isSmallScreen } = useScreenSize();

  return (
    isSmallScreen ? (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
        <AutocompleteInputMobile 
          suggestions={suggestions}
          inputValue={inputValue}
          category={category}
          setCategory={setCategory}
          setInputValue={setInputValue}
          placeholder={placeholder}
          onSearch={onSearch}
        />
        <Box sx={{ paddingTop: spaces.xs, flex: 1, overflowY: 'auto' }} data-testid='suggestions-container'>
          {suggestions.map(suggestion => (
            <AutoCompleteSuggestion
              key={suggestion.value}
              option={suggestion} 
              onSearch={onSearch}
              inputValue={inputValue}
              setInputValue={setInputValue}
              isDesktop={false}
              onClick={() => {}}
            />
          ))}
        </Box>
      </Box>
    ) : (
      <AutocompleteInputDesktop 
        suggestions={suggestions}
        inputValue={inputValue}
        setInputValue={setInputValue}
        category={category}
        setCategory={setCategory}
        placeholder={placeholder}
        onSearch={onSearch}
      />
    )
  );
};

export default SearchBarInputBox;

const getCommonAutocompleteProps = ({
  suggestions,
  inputValue,
  setInputValue,
  placeholder,
  onSearch,
  category,
  setCategory,
}: AutocompleteInputProps) => ({
  options: suggestions,
  freeSolo: true,
  inputValue,
  onInputChange: (_event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  },
  onChange: (_event: any, _newValue: any, reason: AutocompleteChangeReason) => {
    if (reason === 'selectOption') {
      onSearch(typeof _newValue === 'string' ? _newValue : _newValue?.value || '');
    }
  },
  filterOptions: (options: Suggestion[]) => options,
  renderOption: (
    props: any,
    option: Suggestion,
    { inputValue }: { inputValue: AutocompleteRenderOptionState['inputValue'] }
  ) => (
    <AutoCompleteSuggestion
      key={option.value}
      option={option} 
      onSearch={onSearch}
      inputValue={inputValue}
      setInputValue={setInputValue}
      isDesktop={true}
      onClick={props.onClick}
    />
  ),
  renderInput: (params: AutocompleteRenderInputParams) => (
    <InputBox
      renderInputParams={params}
      placeholder={placeholder}
      onSearch={onSearch}
      inputValue={inputValue}
      setInputValue={setInputValue}
      category={category}
      setCategory={setCategory}
    />
  ),
});

const AutocompleteInputMobile: React.FC<AutocompleteInputProps> = ({
  suggestions, inputValue, setInputValue, placeholder, onSearch, category, setCategory }) => (
  <Autocomplete
    {...getCommonAutocompleteProps(
      { suggestions, inputValue, setInputValue, placeholder, onSearch, category, setCategory })
    }
    data-testid='search-bar-input-box-mobile'
    open={false}
  />
);

const DesktopPopper = (props: any) => {
  return (
    <Popper
      {...props}
      sx={{
        '& .MuiAutocomplete-paper': {
          marginTop: spaces.xs,
          elevation: 8,
          borderRadius: borderRadius.md,
          border: `1px solid ${palette.elevation.outlined}`,
          padding: spaces.xs,
          paddingY: '0px',
          '& .MuiAutocomplete-listbox': {
            '::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',  // Firefox
            msOverflowStyle: 'none',  // IE/Edge
          }
        },
      }}
    />
  );
};  

const AutocompleteInputDesktop: React.FC<AutocompleteInputProps> = ({
  suggestions, inputValue, setInputValue, placeholder, onSearch, category, setCategory }) => (
  <Autocomplete
    data-testid='search-bar-input-box-desktop'
    PopperComponent={(props) => <DesktopPopper {...props} />}
    {...getCommonAutocompleteProps(
      { suggestions, inputValue, setInputValue, placeholder, onSearch, category, setCategory }
    )}
  />
);
